import React, { useState } from 'react';
import moment from 'moment';
import html2pdf from 'html2pdf.js';
import logo from '../../../assests/images/logo.png';
import FormFooter from './FormFooter';



/**
 * StatusWaiverSection — Status Waiver letter for import payments / SBLC opening.
 *
 * Corresponds to sblc_section_3 / StatusWaiverNew from the old Best Timbers project.
 * Renders a printable A4 status waiver letter with:
 *  - Logo + file name header (status-logo)
 *  - Letter No / Date
 *  - Addressee block (Branch Manager, South Indian Bank)
 *  - Subject, Reference, Remittance Amount paragraph
 *  - 3 numbered declarations
 *  - Closing paragraphs + Authorised Signatory line
 *  - Download PDF button
 *
 * Props:
 *   retrievedData {object}  Data entry record from useDataEntry()
 */

const SECTION_ID = 'bank_section_4';
const PDF_FILENAME = 'Status-Waiver.pdf';

const PDF_OPTIONS = {
  margin: 0,
  filename: PDF_FILENAME,
  image: { type: 'jpeg', quality: 0.98 },
  html2canvas: {
    scale: 2,
    useCORS: true,
    scrollY: 0,
    windowWidth: document.documentElement.scrollWidth,
    windowHeight: document.documentElement.scrollHeight,
  },
  jsPDF: {
    unit: 'mm',
    format: [210, 350],
    orientation: 'portrait',
  },
  pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
};

const StatusWaiverSection = ({ retrievedData }) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const handleDownload = () => {
    const element = document.getElementById(SECTION_ID);
    if (!element) return;
    setIsProcessing(true);
    html2pdf()
      .set(PDF_OPTIONS)
      .from(element)
      .save()
      .then(() => setIsProcessing(false))
      .catch(() => setIsProcessing(false));
  };

  return (
    <>
      {/* ── Printable Document Area ── */}
      <div
        id={SECTION_ID}
        style={{
          width: '100%',
          maxWidth: '794px',
          minHeight: '1123px',
          background: 'white',

        }}
      >
        <div className="px-4 avoid-page-break">

          {/* Logo + File Name Header */}
          <div className="status-logo py-1">
            <img
              src={logo}
              alt="logo"
              style={{ height: '90%', width: '30%', objectFit: 'contain' }}
            />

            <p className="file-name-title">
              {retrievedData && retrievedData?.fileName}
            </p>
          </div>


          {/* Letter Body */}
          <div className="d-flex flex-column pt-1" style={{ gap: '19px' }}>

            {/* Letter No. + Date */}
            <div className="d-flex justify-content-between" style={{ gap: '19px' }}>
              <span className="status-waiver-content">Letter No.</span>
              <span className="status-waiver-content">
                Date : {moment().format('DD-MM-YYYY')}
              </span>
            </div>

            {/* Addressee */}
            <div className="d-flex flex-column" style={{ gap: '10px' }}>
              <span className="status-waiver-content">To</span>
              <span className="status-waiver-content">The Branch Manager</span>
              <span className="status-waiver-content">Branch :</span>
              <span className="status-waiver-content">The South Indian Bank Ltd</span>
            </div>

            {/* Subject + Body */}
            <div className="pt-2 d-flex flex-column" style={{ gap: '10px' }}>
              <span className="status-waiver-content">Sir/Madam</span>
              <span className="status-waiver-content">
                Sub: Waiver of Status Report- for import and opening SBLC
              </span>
              <span className="status-waiver-content">
                Ref : Payment towards import and Opening SBLC for Imports
              </span>
              <span className="status-waiver-content --para">
                Remittance Amount: FCY USD {retrievedData?.bgAmtAndInterestAmt ? Number(retrievedData.bgAmtAndInterestAmt).toFixed(2) : ''}{' '}
                Favouring:&nbsp;{retrievedData?.exporter || ''}
                F.Z.E Invoice Number {retrievedData?.invoiceNo || ''}&nbsp; dated{' '}
                {retrievedData?.invoiceDt
                  ? moment(retrievedData.invoiceDt).format('DD-MM-YYYY')
                  : ''}
                &nbsp; for FCY USD {retrievedData?.bgAmtAndInterestAmt ? Number(retrievedData.bgAmtAndInterestAmt).toFixed(2) : ''}
              </span>
              <span className="status-waiver-content">
                With reference to the aforesaid Payment towards import and Opening SBLC for
                Imports favouring M/S ,ON A/C OF {retrievedData?.exporter || ''}{' '}
                {retrievedData?.address || ''} for FCY USD{' '}
                {retrievedData?.bgAmtAndInterestAmt ? Number(retrievedData.bgAmtAndInterestAmt).toFixed(2) : ''}&nbsp;submitted by us on{' '}
                {moment().format('DD-MM-YYYY')}, we hereby inform that we:
              </span>
              <span className="status-waiver-content --listPara">
                1. Have been dealing with this supplier for the past 1 years and we are
                confident of contract performance by them/
              </span>
              <span className="status-waiver-content --listPara">
                2. The supplier comes to us through trusted references and hence we are fully
                confident about their ability to perform. (Strike off whichever is not applicable).
              </span>
              <span className="status-waiver-content --listPara">
                3. We have made our own enquiry and is satisfied regarding the suppliers
                credentials. We have also confirmed the genuinity of the above invoice and the
                bank account details provided there in
              </span>
              <span className="status-waiver-content">
                In light of the same, we request you to kindly process import bill in favour of{' '}
                {retrievedData?.exporter || ''} F.Z.E, as we are convinced about the credibility
                of the supplier.
              </span>
              <span className="status-waiver-content">
                We are fully aware of the need for exercising caution in payments for imports to
                suppliers under high risk category without obtaining status report, but we are
                choosing to go ahead with the payment at our own risk and responsibility. We are
                absolving the Bank or its officials from all risks and responsibility that may be
                incurred by us in this import payment
              </span>
              <span className="status-waiver-content">
                We also wish to inform that we expect the shipment to be completed by{' '}
                _____________date and will be submitting the Bill of Entry Number and Date within
                reasonable time of taking delivery and in any case, within 90 days from date of
                remittance/opening of LC.
              </span>
              <span className="status-waiver-content">
                Request you to kindly process the payment/LC opening request without obtaining
                satisfactory credit report as we assume all risk and responsibility for likely losses.
              </span>
              <span className="status-waiver-content pt-3">Yours faithfully</span>
              <span className="status-waiver-content --content-border pt-3 mb-1">
                Authorised Signatory
              </span>
            </div>

          </div>

          <FormFooter />
        </div>
      </div>


      {/* ── Download Button ── */}
      <div className="d-flex justify-content-center py-2">
        <button
          type="button"
          id="status-waiver-download-btn"
          className="btn btn-orange text-uppercase fw-bold px-4 py-2"
          onClick={handleDownload}
          disabled={isProcessing}
        >
          {isProcessing ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Generating PDF...
            </>
          ) : (
            <>📥 Download Status Waiver</>
          )}
        </button>
      </div>
    </>
  );
};

export default StatusWaiverSection;
